import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type {
  CampaignData,
  CollectionKey,
  CollectionItem,
  NewCollectionItem,
  User,
} from '../../shared/types';

const defaultCampaignData: CampaignData = {
  name: '',
  setting: '',
  sessionNumber: 1,
  partyHeat: 0,
  partyHeatNotes: '',
  playerCharacters: [],
  npcs: [],
  locations: [],
  organizations: [],
  plotThreads: [],
  sessionNotes: [],
  calendars: [],
  calendarEvents: [],
  maps: [],
  mapLayers: [],
  mapMarkers: [],
  timelines: [],
  timelineEvents: [],
  futureContent: [],
  wikiArticles: [],
};

const initialCampaignData: CampaignData = {
  name: 'The Unseen Hand',
  setting:
    "Sword Coast spy campaign. The Unseen Hand is a half-forgotten shadow network built by the major factions for deniable missions. Once respected, now dusty and underfunded. PCs are Level 1 operatives from various factions (Emerald Enclave, Harpers, Lords' Alliance, Order of Gauntlet, Purple Dragon Knights, Red Wizards, Zhentarim) sent to revive the organization.",
  sessionNumber: 1,
  partyHeat: 0,
  partyHeatNotes: '',
  playerCharacters: [],
  npcs: [
    {
      id: Date.now(),
      name: 'Robin',
      description: 'Elderly female elf rogue, party handler',
      location: 'The Bat Cave (Neverwinter Wood)',
      notes: 'From the Harpers. Calm and dangerous. Tests PCs constantly. "The Hand comes first, faction politics second."'
    }
  ],
  locations: [
    {
      id: Date.now() + 1,
      name: 'The Coal & Clover',
      description: 'Tavern in Neverwinter, first meeting location',
      connections: 'Neverwinter, Neverwinter Wood',
      notes: 'No uniforms, no faction pins, arrive alone'
    },
    {
      id: Date.now() + 2,
      name: 'The Bat Cave',
      description: 'Unseen Hand hideout in Neverwinter Wood',
      connections: 'Neverwinter Wood, The Coal & Clover',
      notes: "Cave choked with bats and old tunnels. First rule: don't get followed here."
    }
  ],
  organizations: [
    {
      id: Date.now() + 10,
      name: 'The Unseen Hand',
      type: 'Secret Network',
      goals: 'Handle missions too delicate for public action. Stop threats in shadows without starting wars.',
      members: 'Contributors from all major factions',
      notes: 'Half-forgotten, underfunded. Once respected, now a dusty relic most assume is dead or never existed.'
    },
    {
      id: Date.now() + 11,
      name: 'Emerald Enclave',
      type: 'Faction',
      goals:
        'Preserve balance between nature and civilization. Prevent unnatural magic from warping ecosystems. Protect sacred places from political consumption.',
      members: 'Druids, rangers, nature advocates',
      notes:
        'Operates through subtle pressure and sabotage when necessary. Ruthless when the wilds are threatened. Ensures civilization pays a price when it takes too much.'
    },
    {
      id: Date.now() + 12,
      name: 'Harpers',
      type: 'Faction',
      goals:
        'Keep power from becoming tyranny. Protect the vulnerable through information and quiet action. Preserve dangerous knowledge from falling into wrong hands.',
      members: 'Spies, scholars, information brokers',
      notes:
        'Operates through networks, intelligence, truth, and targeted intervention. Exposes corruption before it becomes law. Maintains information advantage.'
    },
    {
      id: Date.now() + 13,
      name: "Lords' Alliance",
      type: 'Faction',
      goals:
        'Stability, prosperity, and control of trade and security across the Sword Coast. Keep trade routes open and profitable. Reduce chaos that threatens cities.',
      members: 'Nobles, merchants, city officials',
      notes:
        'Uses politics, law, money, and force when needed - always with legitimacy as shield. Solves problems quietly to avoid scandal.'
    },
    {
      id: Date.now() + 14,
      name: 'Order of the Gauntlet',
      type: 'Faction',
      goals:
        'Fight evil directly and uphold justice. Eliminate cults, fiends, undead, and corrupting forces. Maintain moral authority.',
      members: 'Paladins, clerics, righteous warriors',
      notes:
        'Operates with zeal, discipline, and moral clarity. Will work in shadow if it ends darkness. Prevents evil from gaining footholds inside institutions.'
    },
    {
      id: Date.now() + 15,
      name: 'Purple Dragon Knights',
      type: 'Faction',
      goals:
        "Protect Cormyr's interests and contain threats before they reach home. Build influence and relationships that benefit Cormyr long-term.",
      members: 'Cormyrean military officers, intelligence agents',
      notes:
        'Military discipline and decisive action. Collects intelligence on rivals, arcane movements, and emerging factions. Maintains order through competence.'
    },
    {
      id: Date.now() + 16,
      name: 'Red Wizards',
      type: 'Faction',
      goals: 'Acquire power through arcane mastery, artifacts, and influence. Secure rare magic and forbidden knowledge. Control magical infrastructure.',
      members: 'Thayan wizards, arcane researchers',
      notes: 'Uses research, leverage, bargains, and calculated risk. Sees people as resources. Turns chaos into opportunity and profit.'
    },
    {
      id: Date.now() + 17,
      name: 'Zhentarim',
      type: 'Faction',
      goals:
        'Wealth and power through control of markets, routes, secrets, and people. Build influence by owning unofficial systems everyone relies on.',
      members: 'Mercenaries, smugglers, merchants',
      notes:
        'Operates through smuggling, mercenary work, intimidation, and businesslike brutality. Accumulates blackmail and favors. Turns every crisis into profit.'
    }
  ],
  plotThreads: [
    {
      id: Date.now() + 3,
      title: 'The Puppet Master',
      description: 'One enemy directing multiple competing interests. Early missions focus on exposure: names, proof, motives.',
      status: 'Active',
      priority: 'High'
    },
    {
      id: Date.now() + 4,
      title: 'Heat System (Party 0-5, Personal 0-5)',
      description: 'Track how loud the party is and how hard the world pushes back. Heat affects scrutiny, checkpoints, warrants, and manhunts.',
      status: 'Active',
      priority: 'Medium'
    }
  ],
  sessionNotes: [],
  calendars: [],
  calendarEvents: [],
  maps: [],
  mapLayers: [],
  mapMarkers: [],
  timelines: [],
  timelineEvents: [],
  futureContent: [
    {
      id: Date.now() + 5,
      title: 'Session 1: The Coal & Clover',
      type: 'Story Beat',
      description: 'First meet at the tavern, activation signals, journey to Bat Cave',
      prepNotes: 'Subtle confirmations (bartender phrase, special coin, marked card, signal). Test if PCs can avoid being followed to hideout.'
    }
  ],
  wikiArticles: []
};

export const normalizePlayerCharacter = (pc: CampaignData['playerCharacters'][number]) => {
  const level = typeof pc.level === 'number' ? pc.level : parseInt(String(pc.level)) || 1;
  const personalHeat =
    typeof pc.personalHeat === 'number' ? pc.personalHeat : parseInt(String(pc.personalHeat)) || 0;
  const factionRelationships = Array.isArray(pc.factionRelationships) ? pc.factionRelationships : [];
  const classes = Array.isArray(pc.classes) ? pc.classes : null;

  return {
    ...pc,
    level,
    personalHeat,
    factionRelationships,
    classes,
  };
};

export function useCampaign(
  currentUser: User | null,
  isAuthenticated: boolean,
  activeCampaignId: number | null
) {
  const [campaignData, setCampaignData] = useState<CampaignData>(initialCampaignData);

  const loadData = useCallback(async () => {
    if (!currentUser) return;

    try {
      const loadedData = await api.getCampaign();
      const playerCharacters = (loadedData.playerCharacters || []).map((pc) =>
        normalizePlayerCharacter(pc)
      );

      setCampaignData({
        name: loadedData.name || '',
        setting: loadedData.setting || '',
        sessionNumber: loadedData.sessionNumber || 1,
        partyHeat: loadedData.partyHeat || 0,
        partyHeatNotes: loadedData.partyHeatNotes || '',
        playerCharacters,
        npcs: loadedData.npcs || [],
        locations: loadedData.locations || [],
        organizations: loadedData.organizations || [],
        plotThreads: loadedData.plotThreads || [],
        sessionNotes: loadedData.sessionNotes || [],
        calendars: loadedData.calendars || [],
        calendarEvents: loadedData.calendarEvents || [],
        maps: loadedData.maps || [],
        mapLayers: loadedData.mapLayers || [],
        mapMarkers: loadedData.mapMarkers || [],
        timelines: loadedData.timelines || [],
        timelineEvents: loadedData.timelineEvents || [],
        futureContent: loadedData.futureContent || [],
        wikiArticles: loadedData.wikiArticles || []
      });
    } catch {
      console.log('No saved campaign data found');
    }
  }, [currentUser]);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadData();
    }
  }, [isAuthenticated, currentUser, activeCampaignId, loadData]);

  const resetCampaignData = () => {
    setCampaignData(defaultCampaignData);
  };

  const normalizeValue = (value: string) => value.trim().toLowerCase();

  const resolveLocationEntity = (name: string) => {
    const target = normalizeValue(name);
    return (campaignData.locations || []).find(
      (loc) => normalizeValue(loc.name) === target
    );
  };

  const resolveLocationById = (id: number | null | undefined) => {
    if (!id) return null;
    return (campaignData.locations || []).find((loc) => loc.id === id) || null;
  };

  const resolveLocationIdByName = (name: string) => {
    const match = resolveLocationEntity(name);
    return match ? match.id : null;
  };

  const addItem = async <K extends CollectionKey>(category: K, item: NewCollectionItem<K>) => {
    try {
      let payload: NewCollectionItem<K> = item;
      if (category === 'npcs') {
        const npc = item as CampaignData['npcs'][number];
        const resolvedId = npc.locationId ?? resolveLocationIdByName(npc.location || '');
        const resolvedName = npc.locationId
          ? resolveLocationById(npc.locationId)?.name || npc.location
          : npc.location;
        payload = {
          ...(npc as unknown as NewCollectionItem<K>),
          locationId: resolvedId,
          location: resolvedName || '',
        };
      }
      const created = await api.createItem(category, payload);
      const normalized =
        category === 'playerCharacters'
          ? (normalizePlayerCharacter(created as CampaignData['playerCharacters'][number]) as CollectionItem<K>)
          : created;
      const newData: CampaignData = {
        ...campaignData,
        [category]: [...campaignData[category], normalized]
      };
      setCampaignData(newData);
      return normalized;
    } catch (error) {
      console.error('Error adding item:', error);
      return undefined;
    }
  };

  const updateItem = async <K extends CollectionKey>(category: K, id: number, updates: Partial<CollectionItem<K>>) => {
    try {
      let payload: Partial<CollectionItem<K>> = updates;
      if (category === 'npcs') {
        const npcUpdate = updates as Partial<CampaignData['npcs'][number]>;
        const nameMatchId = npcUpdate.location ? resolveLocationIdByName(npcUpdate.location) : null;
        const clearedLocation = npcUpdate.location !== undefined && npcUpdate.location.trim() === '';
        const resolvedId = clearedLocation ? null : nameMatchId ?? npcUpdate.locationId ?? null;
        const resolvedName = resolvedId
          ? resolveLocationById(resolvedId)?.name || npcUpdate.location
          : npcUpdate.location;
        payload = {
          ...(npcUpdate as Partial<CollectionItem<K>>),
          ...(resolvedId !== undefined ? { locationId: resolvedId } : {}),
          ...(resolvedName !== undefined ? { location: resolvedName } : {}),
        } as Partial<CollectionItem<K>>;
      }
      const updated = await api.updateItem(category, id, payload);
      const normalized =
        category === 'playerCharacters'
          ? (normalizePlayerCharacter({
              ...(campaignData.playerCharacters.find((item) => item.id === id) || {}),
              ...(updated as CampaignData['playerCharacters'][number]),
            } as CampaignData['playerCharacters'][number]) as CollectionItem<K>)
          : updated;
      const newData: CampaignData = {
        ...campaignData,
        [category]: campaignData[category].map((item) => (item.id === id ? normalized : item))
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const deleteItem = async <K extends CollectionKey>(category: K, id: number) => {
    try {
      await api.deleteItem(category, id);
      const newData: CampaignData = {
        ...campaignData,
        [category]: campaignData[category].filter((item) => item.id !== id)
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const updateCampaignInfo = async (field: keyof CampaignData, value: CampaignData[keyof CampaignData]) => {
    try {
      const updates = { [field]: value };
      await api.updateCampaign(updates);
      setCampaignData({ ...campaignData, [field]: value } as CampaignData);
    } catch (error) {
      console.error('Error updating campaign info:', error);
    }
  };

  const updatePartyHeat = (value: string) => {
    const heat = Math.max(0, Math.min(5, parseInt(value) || 0));
    updateCampaignInfo('partyHeat', heat);
  };

  const updatePCHeat = (pcId: number, heat: string) => {
    const newHeat = Math.max(0, Math.min(5, parseInt(heat) || 0));
    updateItem('playerCharacters', pcId, { personalHeat: newHeat });
  };

  const addFactionRelationship = async (pcId: number, faction: string, renown = 1) => {
    const pc = campaignData.playerCharacters.find((p) => p.id === pcId);
    if (!pc) return;

    try {
      const newRelationship = await api.addFactionRelationship(pcId, faction, parseInt(String(renown)) || 1);
      const relationships = [...(pc.factionRelationships || []), newRelationship];
      const newData: CampaignData = {
        ...campaignData,
        playerCharacters: campaignData.playerCharacters.map((p) =>
          p.id === pcId ? { ...p, factionRelationships: relationships } : p
        )
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error adding faction relationship:', error);
    }
  };

  const updateFactionRenown = async (pcId: number, relationshipId: number, newRenown: string) => {
    const pc = campaignData.playerCharacters.find((p) => p.id === pcId);
    if (!pc) return;

    try {
      const updatedRel = await api.updateFactionRelationship(pcId, relationshipId, Math.max(1, Math.min(25, parseInt(newRenown) || 1)));
      const relationships = (pc.factionRelationships || []).map((rel) =>
        rel.id === relationshipId ? updatedRel : rel
      );
      const newData: CampaignData = {
        ...campaignData,
        playerCharacters: campaignData.playerCharacters.map((p) =>
          p.id === pcId ? { ...p, factionRelationships: relationships } : p
        )
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error updating faction renown:', error);
    }
  };

  const removeFactionRelationship = async (pcId: number, relationshipId: number) => {
    const pc = campaignData.playerCharacters.find((p) => p.id === pcId);
    if (!pc) return;

    try {
      await api.deleteFactionRelationship(pcId, relationshipId);
      const relationships = (pc.factionRelationships || []).filter((rel) => rel.id !== relationshipId);
      const newData: CampaignData = {
        ...campaignData,
        playerCharacters: campaignData.playerCharacters.map((p) =>
          p.id === pcId ? { ...p, factionRelationships: relationships } : p
        )
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error removing faction relationship:', error);
    }
  };

  const addSessionLinkedItem = async (sessionId: number, category: string, itemId: number) => {
    try {
      const linked = await api.addSessionLinkedItem(sessionId, category, itemId);
      const newData: CampaignData = {
        ...campaignData,
        sessionNotes: campaignData.sessionNotes.map((sn) =>
          sn.id === sessionId
            ? { ...sn, linkedItems: [...(sn.linkedItems || []), linked] }
            : sn
        ),
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error linking item to session:', error);
    }
  };

  const removeSessionLinkedItem = async (sessionId: number, linkId: number) => {
    try {
      await api.removeSessionLinkedItem(sessionId, linkId);
      const newData: CampaignData = {
        ...campaignData,
        sessionNotes: campaignData.sessionNotes.map((sn) =>
          sn.id === sessionId
            ? { ...sn, linkedItems: (sn.linkedItems || []).filter((li) => li.id !== linkId) }
            : sn
        ),
      };
      setCampaignData(newData);
    } catch (error) {
      console.error('Error unlinking item from session:', error);
    }
  };

  return {
    campaignData,
    setCampaignData,
    reloadCampaignData: loadData,
    resetCampaignData,
    addItem,
    updateItem,
    deleteItem,
    updateCampaignInfo,
    updatePartyHeat,
    updatePCHeat,
    addFactionRelationship,
    updateFactionRenown,
    removeFactionRelationship,
    addSessionLinkedItem,
    removeSessionLinkedItem,
    resolveLocationEntity,
    resolveLocationById,
    resolveLocationIdByName,
    normalizeValue,
  };
}
